import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useDeckStore } from '../store/deckStore';
import { LAYOUT_META } from '../lib/layouts';
import { ASSETS, BRAND, BRAND_COLORS, THEMES } from '../lib/brand';
import type { ElementType, MasterLayoutId } from '../types/slide';
import type { SlideAiAction } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { ConfirmDialog } from './ConfirmDialog';
import { ElementSettings } from './ElementSettings';
import { PublishTemplateDialog } from './admin/PublishTemplateDialog';

const ELEMENT_NAMES: Record<ElementType, string> = {
  text: 'Text',
  shape: 'Shape',
  image: 'Image',
  kpi_card: 'KPI card',
  pros_cons: 'Pros / cons',
  callout: 'Callout',
  process_row: 'Process',
  chart: 'Chart',
  timeline: 'Timeline',
  logo: 'Company logo',
  footer: 'Confidentiality footer',
  slide_number: 'Slide number',
  quote_block: 'Quote',
  checklist: 'Checklist',
  stat_row: 'Stat row',
  team_card: 'Team card',
};

function Section({ label, children, divided }: { label: string; children: React.ReactNode; divided?: boolean }) {
  return (
    <Stack
      component="section"
      spacing={1}
      sx={divided ? { pt: 2.5, borderTop: 1, borderColor: 'chrome.subtle' } : undefined}
    >
      <FormLabel component="h3" sx={{ m: 0 }}>
        {label}
      </FormLabel>
      {children}
    </Stack>
  );
}

function SlideAiPanel() {
  const applySlideAi = useDeckStore((s) => s.applySlideAi);
  const aiEnabled = useAuthStore((s) => s.aiEnabled);
  const [busy, setBusy] = useState<SlideAiAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!aiEnabled) return null;

  const run = async (action: SlideAiAction) => {
    setBusy(action);
    setError(null);
    try {
      await applySlideAi(action);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI edit failed');
    } finally {
      setBusy(null);
    }
  };

  const actions: { id: SlideAiAction; label: string; hint: string }[] = [
    { id: 'tighten', label: 'Tighten this slide', hint: 'Rewrite copy for executives' },
    { id: 'add_kpi', label: 'Add a KPI row', hint: 'Insert metric cards from the content' },
    { id: 'notes_to_bullets', label: 'Notes → bullets', hint: 'Turn speaker notes into slide bullets' },
  ];

  return (
    <Section label="AI for this slide" divided>
      <Stack spacing={1}>
        {actions.map((a) => (
          <Button
            key={a.id}
            size="small"
            variant="outlined"
            color="neutral"
            disabled={Boolean(busy)}
            startIcon={
              busy === a.id ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeOutlinedIcon color="primary" />
            }
            onClick={() => void run(a.id)}
            sx={{ justifyContent: 'flex-start' }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                {a.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="span">
                {a.hint}
              </Typography>
            </Box>
          </Button>
        ))}
      </Stack>
      <Snackbar open={Boolean(error)} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Section>
  );
}

function SlideSettings() {
  const slide = useDeckStore((s) => s.slides[s.activeSlideIndex]);
  const applyLayout = useDeckStore((s) => s.applyLayout);
  const setSlideBackground = useDeckStore((s) => s.setSlideBackground);
  const selectedId = useDeckStore((s) => s.selectedElementId);
  const setSlideNotes = useDeckStore((s) => s.setSlideNotes);
  if (!slide) return null;

  const selected = slide.elements.find((e) => e.id === selectedId);
  const locked = slide.elements.filter((e) => e.locked);
  const bg = slide.backgroundImage ? null : slide.background.toUpperCase();
  const photoActive = slide.backgroundImage === ASSETS.coverBackground;

  return (
    <Stack spacing={2.5}>
      {selected && !selected.locked && (
        <Section label={selected.name ?? ELEMENT_NAMES[selected.type]}>
          <ElementSettings key={selected.id} el={selected} />
        </Section>
      )}

      <SlideAiPanel />

      <Section label="Layout" divided>
        <Select
          fullWidth
          value={slide.layoutId}
          onChange={(e) => applyLayout(e.target.value as MasterLayoutId)}
          inputProps={{ 'aria-label': 'Layout' }}
        >
          {LAYOUT_META.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name}
            </MenuItem>
          ))}
        </Select>
      </Section>

      <Section label="Background">
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Tooltip title="Template photo">
            <ButtonBase
              aria-label="Template photo"
              aria-pressed={photoActive}
              onClick={() => setSlideBackground(BRAND.blue, ASSETS.coverBackground)}
              sx={(t) => ({
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundImage: `url(${ASSETS.coverBackground})`,
                backgroundSize: 'cover',
                border: photoActive ? `2px solid ${t.palette.background.paper}` : `1px solid ${t.palette.chrome.border}`,
                boxShadow: photoActive ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
              })}
            />
          </Tooltip>
          {BRAND_COLORS.map((c) => {
            const active = bg === c.hex;
            return (
              <Tooltip key={c.hex} title={c.name}>
                <ButtonBase
                  aria-label={c.name}
                  aria-pressed={active}
                  onClick={() => setSlideBackground(c.hex)}
                  sx={(t) => ({
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: c.hex,
                    border: active ? `2px solid ${t.palette.background.paper}` : `1px solid ${t.palette.chrome.border}`,
                    boxShadow: active ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                  })}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </Section>

      <Section label="Speaker notes" divided>
        <TextField
          key={slide.id}
          multiline
          minRows={3}
          maxRows={10}
          fullWidth
          placeholder="Notes for the presenter. Exported to PowerPoint notes."
          defaultValue={slide.notes ?? ''}
          onChange={(e) => setSlideNotes(e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': 'Speaker notes' } }}
        />
      </Section>

      {locked.length > 0 && (
        <Section label="Brand elements" divided>
          <Stack>
            {locked.map((el) => (
              <Stack
                key={el.id}
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.75,
                  px: 0.75,
                  mx: -0.75,
                  borderRadius: 1,
                  bgcolor: el.id === selectedId ? 'chrome.tint' : 'transparent',
                }}
              >
                <Typography variant="body2" sx={{ color: 'neutral.main' }}>
                  {el.name ?? ELEMENT_NAMES[el.type]}
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockOutlinedIcon sx={{ fontSize: 14 }} /> Locked
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Section>
      )}
    </Stack>
  );
}

function DeckSettings() {
  const theme = useDeckStore((s) => s.theme);
  const setTheme = useDeckStore((s) => s.setTheme);
  const slideCount = useDeckStore((s) => s.slides.length);
  const lastSavedAt = useDeckStore((s) => s.lastSavedAt);
  const reapplyTemplate = useDeckStore((s) => s.reapplyTemplate);
  const deckId = useDeckStore((s) => s.deckId);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [confirmTemplate, setConfirmTemplate] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  return (
    <Stack spacing={2.5}>
      <Section label="Theme">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          {Object.values(THEMES).map((t) => {
            const active = theme === t.id;
            return (
              <ButtonBase
                key={t.id}
                aria-pressed={active}
                onClick={() => setTheme(t.id)}
                sx={(th) => ({
                  justifyContent: 'flex-start',
                  gap: 1,
                  p: 1,
                  borderRadius: 2,
                  fontSize: 12,
                  fontWeight: 500,
                  border: active ? `1.5px solid ${th.palette.primary.main}` : `1px solid ${th.palette.chrome.border}`,
                  bgcolor: active ? alpha(th.palette.primary.main, 0.04) : 'background.paper',
                })}
              >
                <Box sx={{ display: 'flex', borderRadius: 0.75, overflow: 'hidden', flexShrink: 0 }}>
                  <Box sx={{ width: 12, height: 20, bgcolor: t.titleBg }} />
                  <Box sx={{ width: 12, height: 20, bgcolor: t.accent }} />
                </Box>
                {t.name}
              </ButtonBase>
            );
          })}
        </Box>
      </Section>

      <Section label="Details" divided>
        <Stack spacing={0.75}>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Slides</Typography>
            <Typography variant="body2">{slideCount}</Typography>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Format</Typography>
            <Typography variant="body2">16:9 · 1280 × 720</Typography>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Last saved</Typography>
            <Typography variant="body2">
              {lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            </Typography>
          </Stack>
        </Stack>
      </Section>

      <Section label="Template" divided>
        <Button
          variant="outlined"
          color="neutral"
          startIcon={<AutoFixHighOutlinedIcon />}
          onClick={() => setConfirmTemplate(true)}
        >
          Re-apply AST template
        </Button>
        <Typography variant="caption">
          Rebuilds every slide in the current template. Useful for decks made before a template update.
        </Typography>
        {isAdmin && (
          <Button variant="outlined" color="neutral" disabled={!deckId} onClick={() => setPublishOpen(true)}>
            Publish as company template
          </Button>
        )}
        {isAdmin && <PublishTemplateDialog open={publishOpen} onClose={() => setPublishOpen(false)} />}
        <ConfirmDialog
          open={confirmTemplate}
          title="Re-apply the AST template?"
          message="Every slide is rebuilt in its layout. Titles, text, KPI cards, charts, timelines and speaker notes are kept; images and blocks you added by hand are removed. You can undo this."
          confirmLabel="Re-apply template"
          onConfirm={reapplyTemplate}
          onClose={() => setConfirmTemplate(false)}
        />
      </Section>

      <Section label="Brand palette" divided>
        <Stack direction="row" spacing={0.75}>
          {BRAND_COLORS.map((c) => (
            <Tooltip key={c.hex} title={`${c.name} ${c.hex}`}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  bgcolor: c.hex,
                  border: c.hex === BRAND.white || c.hex === BRAND.surface ? 1 : 0,
                  borderColor: 'chrome.border',
                }}
              />
            </Tooltip>
          ))}
        </Stack>
        <Typography variant="caption">Slides can only use these colors.</Typography>
      </Section>
    </Stack>
  );
}

export function Inspector() {
  const [tab, setTab] = useState<'slide' | 'deck'>('slide');

  return (
    <Box
      component="aside"
      aria-label="Settings"
      sx={{
        width: 280,
        flexShrink: 0,
        overflowY: 'auto',
        p: 2,
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={tab}
        onChange={(_, v: 'slide' | 'deck' | null) => v && setTab(v)}
        aria-label="Settings scope"
        sx={{ mb: 2.5 }}
      >
        <ToggleButton value="slide">Slide</ToggleButton>
        <ToggleButton value="deck">Deck</ToggleButton>
      </ToggleButtonGroup>
      {tab === 'slide' ? <SlideSettings /> : <DeckSettings />}
    </Box>
  );
}
